import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api, getApiError } from "../../api/client";
import { useWorkspace } from "../../context/WorkspaceContext";
import FormField from "../components/ui/FormField";
import DeploymentTargetCard from "../components/Cards/DeploymentTargetCard";
import FileUpload from "../components/ui/FileUpload";
import { NEXT_STEPS, TARGETS } from "../constants";

export default function NewDeploymentPage() {
  const navigate = useNavigate();
  const { currentEngagementId } = useWorkspace();
  const [form, setForm] = useState({
    repo_url: "",
    git_ref: "main",
    target: "gcp",
    health_check_url: "",
    is_private_repo: false,
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadKey, setUploadKey]         = useState(0);
  const [submitting, setSubmitting]       = useState(false);
  const [error, setError]                 = useState("");
  const [repoUrlError, setRepoUrlError]   = useState(false);
  const [gitRefError, setGitRefError]     = useState(false);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleRepoUrlChange = (e) => {
    setForm((prev) => ({ ...prev, repo_url: e.target.value }));
    if (repoUrlError && e.target.value.trim()) setRepoUrlError(false);
  };

  const handleGitRefChange = (e) => {
    setForm((prev) => ({ ...prev, git_ref: e.target.value }));
    if (gitRefError && e.target.value.trim()) setGitRefError(false);
  };

  const handleFileChange = (files) => setSelectedFiles(files);
  const resetForm = () => {
    setForm({ repo_url: "", git_ref: "main", target: "gcp", health_check_url: "", is_private_repo: false });
    setSelectedFiles([]);
    setUploadKey((k) => k + 1);
    setError("");
    setRepoUrlError(false);
    setGitRefError(false);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    const hasRepoError = !form.repo_url.trim();
    const hasGitError  = !form.git_ref.trim();
    setRepoUrlError(hasRepoError);
    setGitRefError(hasGitError);
    if (hasRepoError || hasGitError) return;

    if (!currentEngagementId) {
      setError("Select a project in the switcher above before submitting a deployment.");
      return;
    }

    setError("");
    setSubmitting(true);

    const fd = new FormData();
    fd.append("repo_url",        form.repo_url.trim());
    fd.append("git_ref",         form.git_ref.trim() || "main");
    fd.append("target",          form.target);
    fd.append("engagement_id",   currentEngagementId);
    fd.append("is_private_repo", form.is_private_repo ? "true" : "false");
    if (form.health_check_url.trim()) fd.append("health_check_url", form.health_check_url.trim());
    if (selectedFiles.length > 0)    fd.append("doc", selectedFiles[0]);

    try {
      const { data } = await api.post("/golive/deployment_requests", fd);
      navigate(`/golive/${data.id}`);
    } catch (err) {
      setError(getApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="gl-page">
      <div className="mb-6">
        <h1 className="gl-h1">Submit a deployment</h1>
        <p className="mt-1 text-sm text-slate-500">
          The AI will analyze your repository and propose what needs to be set up.
          You'll review before anything runs.
        </p>
      </div>

      {/* Two-column layout */}
      <div className="flex gap-6 items-start">
        <div className="flex-1 bg-white border text-slate-800 border-gray-200 rounded-xl p-6 shadow-[var(--jade-sh-1)]">
          <div className="mb-5">
            <p className="text-lg font-semibold ">Repository &amp; target</p>
            <p className="text-xs  mt-0.5">Required to start a run</p>
          </div>

          {error && <div className="gl-error mb-4">{error}</div>}

          <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
            <FormField
              label="Git repository URL"
              required
              helperText="HTTPS URL for public repos. For private repos, enable the toggle below."
              error={repoUrlError}
              errorText="Repository URL is required."
            >
              <input
                className={`gl-input${repoUrlError ? " gl-input-error" : ""}`}
                name="repo_url"
                value={form.repo_url}
                onChange={handleRepoUrlChange}
                placeholder="https://github.com/acme/orders-service"
              />
            </FormField>

            <label className="flex items-center gap-3 cursor-pointer select-none w-fit">
              <div
                role="switch"
                aria-checked={form.is_private_repo}
                onClick={() => setForm((f) => ({ ...f, is_private_repo: !f.is_private_repo }))}
                className={[
                  "relative w-9 h-5 rounded-full transition-colors",
                  form.is_private_repo ? "bg-[var(--jade-blue-700)]" : "bg-gray-300",
                ].join(" ")}
              >
                <span className={[
                  "absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform",
                  form.is_private_repo ? "translate-x-4" : "translate-x-0",
                ].join(" ")} />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-700">Private repository</p>
                <p className="text-xs text-slate-400">GoLive will generate an SSH deploy key for you to add to your repo.</p>
              </div>
            </label>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                label="Branch or commit SHA"
                helperText="Defaults to main."
                error={gitRefError}
                errorText="Branch or commit SHA is required."
              >
                <input
                  className={`gl-input${gitRefError ? " gl-input-error" : ""}`}
                  name="git_ref"
                  value={form.git_ref}
                  onChange={handleGitRefChange}
                  placeholder="main"
                />
              </FormField>

              <FormField label="Health-check URL" badge="Optional" helperText="Polled after deploy to confirm *live*.">
                <input
                  className="gl-input"
                  name="health_check_url"
                  value={form.health_check_url}
                  onChange={handleChange}
                  placeholder="https://app/health (optional)"
                      />
              </FormField>
            </div>
            <div>
              <label className="text-sm font-semibold text-slate-800">
Supporting  documents
        <span className="ml-2 text-xs px-2 py-0.5 rounded-full border border-gray-300 text-gray-400 font-normal">
          Optional
        </span>
      </label>
            </div>

            <FileUpload key={uploadKey} files={selectedFiles} onChange={handleFileChange} />

            <div>
              <p className="text-sm font-semibold text-slate-800 mb-3">
                Deployment target <span className="text-red-500">*</span>
              </p>
              <div className="grid grid-cols-2 gap-3">
                {TARGETS.map((t) => (
                  <DeploymentTargetCard
                    key={t.value}
                    value={t.value}
                    selected={form.target === t.value}
                    onChange={handleChange}
                    title={t.title}
                    description={t.description}
                  />
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="gl-btn"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button type="submit" className="gl-btn primary" disabled={submitting}>
                {submitting ? "Submitting…" : <><span>Start Analysis</span><ArrowRight size={14} /></>}
              </button>
            </div>
          </form>
        </div>
        <div className="w-72 shrink-0 bg-white border border-gray-200 rounded-xl p-5 shadow-[var(--jade-sh-1)]">
          <p className="text-sm font-semibold text-slate-800 mb-4">What happens next</p>
          <ol className="flex flex-col gap-3">
            {NEXT_STEPS.map((step, i) => (
              <li
                key={i}
                className={[
                  "flex items-start gap-3 text-sm",
                  step.highlight ? "text-[var(--jade-warning-700)] font-semibold" : "text-slate-600",
                ].join(" ")}
              >
                <span
                  className={[
                    "shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold",
                    step.highlight
                      ? "bg-[var(--jade-warning-100)] text-[var(--jade-warning-700)]"
                      : "bg-[var(--jade-blue-100)] text-[var(--jade-blue-700)]",
                  ].join(" ")}
                >
                  {i + 1}
                </span>
                {step.text}
              </li>
            ))}
          </ol>
        </div>

      </div>
    </div>
  );
}
